// Auth Context für globales State Management

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../api/authService';
import { UserInfo, LoginCredentials, TwoFactorCredentials, TenantInfo, AuthResponse } from '../types/auth.types';
import { clearPermissionsCache } from '../hooks/usePermissions';

interface AuthContextType {
  user: UserInfo | null;
  currentTenant: TenantInfo | null;
  loading: boolean;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  tempPassword: string | null;
  login: (credentials: LoginCredentials) => Promise<any>;
  verifyTwoFactor: (credentials: TwoFactorCredentials) => Promise<AuthResponse>;
  selectTenant: (tenantId: string, refreshToken: string) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [currentTenant, setCurrentTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // User und Tenant beim App-Start laden
  useEffect(() => {
    loadUser();
    // Gespeicherten Tenant laden
    const storedTenant = authService.getCurrentTenant();
    if (storedTenant) {
      setCurrentTenant(storedTenant);
    }
  }, []);

  const loadUser = async () => {
    if (authService.isAuthenticated()) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Failed to load user:', error);
        authService.logout();
      }
    }
    setLoading(false);
  };

  const login = async (credentials: LoginCredentials) => {
    const response = await authService.login(credentials);

    // Passwort temporär speichern (für 2FA, Tenant-Auswahl oder Passwortänderung)
    setTempPassword(credentials.password);

    // Wenn 2FA benötigt wird
    if (response.requiresTwoFactor) {
      return response;
    }

    // Wenn Tenant-Auswahl benötigt wird
    if (response.requiresTenantSelection) {
      return response;
    }

    // Kein 2FA - User laden
    if (response.user) {
      setUser(response.user);
    } else {
      await loadUser();
    }

    // Tenant setzen
    if (response.currentTenant) {
      setCurrentTenant(response.currentTenant);
    }

    // Passwortänderung erforderlich?
    if (response.mustChangePassword) {
      setMustChangePassword(true);
    } else {
      // Wenn kein Passwortwechsel nötig, tempPassword löschen
      setTempPassword(null);
    }

    return response;
  };

  const verifyTwoFactor = async (credentials: TwoFactorCredentials): Promise<AuthResponse> => {
    const response = await authService.verifyTwoFactor(credentials);

    if (response.user) {
      setUser(response.user);
    } else {
      await loadUser();
    }

    // Passwortänderung erforderlich?
    if (response.mustChangePassword) {
      setMustChangePassword(true);
      // tempPassword bleibt vom Login erhalten
    } else {
      setTempPassword(null);
    }

    return response;
  };

  const selectTenant = async (tenantId: string, refreshToken: string) => {
    const response = await authService.selectTenant(tenantId, refreshToken);

    if (response.user) {
      setUser(response.user);
    } else {
      await loadUser();
    }

    if (response.currentTenant) {
      setCurrentTenant(response.currentTenant);
    }

    if (response.mustChangePassword) {
      setMustChangePassword(true);
      // tempPassword bleibt vom Login erhalten
    } else {
      setTempPassword(null);
    }
  };

  const switchTenant = async (tenantId: string) => {
    const response = await authService.switchTenant(tenantId);

    if (response.user) {
      setUser(response.user);
    } else {
      await loadUser();
    }

    if (response.currentTenant) {
      setCurrentTenant(response.currentTenant);
    }

    clearPermissionsCache(); // Permissions neu laden für neuen Tenant
  };

  const logout = async () => {
    await authService.logout();
    clearPermissionsCache(); // Permissions-Cache leeren bei Logout
    setUser(null);
    setCurrentTenant(null);
    setMustChangePassword(false);
    setTempPassword(null);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await authService.changePassword(currentPassword, newPassword);
    setMustChangePassword(false);
    setTempPassword(null);
  };

  const refreshUser = async () => {
    if (authService.isAuthenticated()) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const value: AuthContextType = {
    user,
    currentTenant,
    loading,
    isAuthenticated: !!user,
    mustChangePassword,
    tempPassword,
    login,
    verifyTwoFactor,
    selectTenant,
    switchTenant,
    logout,
    refreshUser,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom Hook für Auth Context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
